"use client"

import { RadialBarChart, RadialBar, LabelList, Legend } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Flame, Wheat, Beef, Cookie } from "lucide-react";

export type NutritionData = {
  nutrient: string;
  label: string;
  value: number;
  fill: string;
  total: number;
}[];

interface NutritionRingsProps {
  data: NutritionData;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
}

const chartConfig = {
  nutrition: {
    label: "Daily Nutrition",
  },
  calories: {
    label: "Calories",
    color: "hsl(24, 96%, 53%)", // Orange
  },
  carbs: {
    label: "Carbs", 
    color: "hsl(47, 96%, 53%)", // Yellow
  },
  protein: {
    label: "Protein",
    color: "hsl(0, 84%, 53%)", // Red
  },
  fat: {
    label: "Fat",
    color: "hsl(214, 84%, 53%)", // Blue
  },
} satisfies ChartConfig;

const sizes = {
  sm: {
    width: 40,
    height: 40,
    innerRadius: 8,
    outerRadius: 16,
    cornerRadius: 2,
    maxBarSize: 6
  },
  md: {
    width: 250,
    height: 250,
    innerRadius: 40,
    outerRadius: 110,
    cornerRadius: 12,
    maxBarSize: 20
  },
  lg: {
    width: 350,
    height: 350,
    innerRadius: 40,
    outerRadius: 150,
    cornerRadius: 20,
    maxBarSize: 25
  }
};

const NutrientIcon = ({ nutrient }: { nutrient: string }) => {
  switch (nutrient) {
    case "Calories":
      return <Flame className="h-4 w-4 text-orange-500" />;
    case "Carbs":
      return <Wheat className="h-4 w-4 text-yellow-600" />;
    case "Protein":
      return <Beef className="h-4 w-4 text-red-500" />;
    case "Fat":
      return <Cookie className="h-4 w-4 text-blue-500" />;
    default:
      return null;
  }
};

export function NutritionRings({ 
  data, 
  size = "md", 
  showLabel = true
}: NutritionRingsProps) {
  const dimensions = sizes[size];

  return (
    <ChartContainer
      config={chartConfig}
      className="mx-auto aspect-square w-full h-full max-w-[250px]"
    >
      <RadialBarChart
        width={dimensions.width}
        height={dimensions.height}
        data={data}
        innerRadius={dimensions.innerRadius}
        outerRadius={dimensions.outerRadius}
        startAngle={90}
        endAngle={-270}
        cy={dimensions.height / 2}
        cx={dimensions.width / 2}
      >
        {showLabel && (
          <ChartTooltip
            cursor={false}
            content={({ payload }) => {
              if (payload && payload[0]) {
                const data = payload[0].payload;
                return (
                  <div className="flex items-center gap-2 p-2 rounded-lg shadow bg-white">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: data.fill }}
                    />
                    <p className="text-sm text-gray-500">{data.nutrient}</p>
                    <p className="text-sm font-medium">
                      {data.total}
                      {data.nutrient === "Calories" ? " cal" : " g"}
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
        )}
        <RadialBar
          dataKey="value"
          background
          cornerRadius={dimensions.cornerRadius}
          maxBarSize={dimensions.maxBarSize}
        >
          {/* TODO: Add label inside the chart. DO NOT DELETE THIS COMMENT */}
          {/* {showLabel && size !== "sm" && (
            <LabelList
              dataKey="nutrient" 
              position="insideStart"
              offset={-dimensions.maxBarSize}
              content={({ value }) => (
                <NutrientIcon nutrient={value as string} />
              )}
            />
          )} */}
        </RadialBar>
        {/* TODO: Add legend. DO NOT DELETE THIS COMMENT */}
        {/* <Legend iconSize={10} width={120} height={140} layout='vertical' verticalAlign='bottom' align="right" /> */}
      </RadialBarChart>
    </ChartContainer>
  );
}